import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import Autocomplete from '@mui/material/Autocomplete';
import type { TextFieldProps } from '@mui/material/TextField';
import { useUpdateEffect } from '@src/corelib/hooks';
import { noop } from '@src/utils';
import React, { useMemo, useRef, useState } from 'react';

import { SyntheticEvent } from '../primitives';
import TextField from './TextField';

// Type email raise warning
// https://www.chromium.org/developers/design-documents/create-amazing-password-forms

const DOMAINS: string[] = [
  // 'gmail.com',
  // 'outlook.com',
  // 'hotmail.com',
  // 'icloud.com',
];

const styles = {
  paper: {
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
};

function createOptions(value: string): string[] {
  if (!value.trim() || DOMAINS.length === 0) {
    return [];
  }

  const [namePart, domainPart] = value.split('@');

  // When no '@' is typed, suggest all domains
  if (!domainPart) {
    return DOMAINS.map((domain) => `${namePart}@${domain}`);
  }
  // When '@' is typed without a domain, still suggest all domains
  if (domainPart === '') {
    return DOMAINS.map((domain) => `${namePart}@${domain}`);
  }

  return DOMAINS.filter((domain) => domain.startsWith(domainPart)).map(
    (domain) => `${namePart}@${domain}`,
  );
}

interface EmailFieldProps extends Omit<TextFieldProps, 'onChange'> {
  value?: string;
  onChange?: (event: SyntheticEvent) => void;
}

const EmailField = React.forwardRef<HTMLDivElement, EmailFieldProps>(
  (props, ref) => {
    const {
      value = '',
      onChange = noop,
      error = false,
      fullWidth = false,
      helperText = ' ',
      name,
      onBlur = noop,
      placeholder,
      slotProps: slotPropsProp,
      ...restProps
    } = props;

    const inputRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState(value);
    const options = useMemo(() => createOptions(inputValue), [inputValue]);

    useUpdateEffect(() => {
      const event = new SyntheticEvent('change', name, inputValue);
      onChange(event);
    }, [inputValue]);

    const handleInputChange = (
      event: React.SyntheticEvent & {
        target: { value?: string; textContent?: string | null };
      },
    ) => {
      switch (event.type) {
        case 'change':
          setInputValue((event.target as HTMLInputElement).value);
          break;
        case 'click':
          setInputValue((event.target as HTMLElement).textContent ?? '');
          break;
        default:
          console.warn(`The event with type=${event.type} is not supported.`);
      }
    };

    const renderInput = (params: AutocompleteRenderInputParams) => {
      return (
        <TextField
          {...restProps}
          ref={ref}
          name={name}
          type="text"
          error={error}
          id={params.id}
          inputRef={inputRef}
          helperText={helperText}
          placeholder={placeholder}
          disabled={params.disabled}
          fullWidth={fullWidth}
          autoComplete="email username"
          onBlur={onBlur}
          slotProps={{
            ...slotPropsProp,
            input: {
              ...params.InputProps,
              ...(slotPropsProp?.input as Record<string, unknown>),
            },
            htmlInput: {
              ...params.inputProps,
              ...(slotPropsProp?.htmlInput as Record<string, unknown>),
            },
            inputLabel: {
              ...params.InputLabelProps,
              ...(slotPropsProp?.inputLabel as Record<string, unknown>),
            },
          }}
        />
      );
    };

    return (
      <Autocomplete
        freeSolo
        openOnFocus
        disablePortal
        size="small"
        options={options}
        value={inputValue}
        fullWidth={fullWidth}
        disableClearable={!inputValue}
        slotProps={{
          paper: {
            sx: styles.paper,
          },
        }}
        onInputChange={handleInputChange}
        renderInput={renderInput}
      />
    );
  },
);

EmailField.displayName = 'EmailField';
export default EmailField;
